import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '@clerk/clerk-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Camera, Loader2, Plus, Upload } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { formatPhone } from '@/lib/format'
import { PhoneLink } from '@/components/PhoneLink'
import { decodeVin, isValidVin, type DecodedVin } from '@/lib/vin'

type CustomerListRow = {
  id: string
  name: string
  phone: string | null
  email: string | null
}

type FromPhotoResponse = {
  vin: string | null
  confidence: 'high' | 'medium' | 'low'
  photo_r2_key: string
  reason?: string
}

// Minimal slice of the worker's EricFocus the owner-prefill needs (id + name).
// Source: the TaskEric paperclip handoff (?customer_id&customer_name) or the
// /api/eric/dashboard-focus backstop. Only these two fields are used here.
type DashboardFocus = { customerId: string; customerName: string | null }

export function VehicleFromPhoto() {
  const { t } = useTranslation('vehicles')
  const { t: tCommon } = useTranslation('common')
  const { getToken, isLoaded } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [file, setFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [dragActive, setDragActive] = useState(false)

  // Pattern B (mount-once probe): hydrate the extraction + VIN from the
  // TaskEric paperclip handoff before first render so we don't have to
  // run a useEffect that writes state. When the dashboard attach-photo
  // flow classifies an image as a VIN sticker, it navigates here with
  // ?r2_key=...&vin=... so we can skip re-uploading the same photo.
  const [searchParams] = useSearchParams()
  const [extraction, setExtraction] = useState<FromPhotoResponse | null>(() => {
    const r2 = searchParams.get('r2_key')
    if (!r2) return null
    const handoffVin =
      searchParams.get('vin')?.trim().toUpperCase() ?? ''
    return {
      vin: handoffVin || null,
      confidence: handoffVin ? 'medium' : 'low',
      photo_r2_key: r2,
    }
  })
  const [extractionError, setExtractionError] = useState<string | null>(null)

  // Editable VIN — pre-filled by extraction; mechanic can correct it on
  // medium/low confidence or from the manual fallback.
  const [vin, setVin] = useState(
    () => searchParams.get('vin')?.trim().toUpperCase() ?? '',
  )

  // Customer picker — same shape as NewEstimate.tsx (per CLAUDE.md, reuse
  // the picker pattern). Could be lifted into a shared component later.
  //
  // Owner is pre-filled from the conversation Eric had just before the photo:
  // the TaskEric paperclip handoff passes ?customer_id&customer_name (the
  // customer the mechanic established — "add a VIN to Levan's Mercedes"), and a
  // reload/deep-link falls back to the /api/eric/dashboard-focus backstop
  // (focusQuery below). It's a default, not a lock — the mechanic can change or
  // clear it, preserving the orphan-vehicle path.
  const handoffCustomerName = searchParams.get('customer_name')?.trim() || null
  const [customerId, setCustomerId] = useState<string | null>(
    () => searchParams.get('customer_id')?.trim() || null,
  )
  // Name to show on the owner chip before the customers list resolves the id
  // (and the only name source if the owner isn't in the first list page).
  const [prefillName, setPrefillName] = useState<string | null>(
    handoffCustomerName,
  )
  // Whether the current owner came from Eric (vs an explicit pick) — drives the
  // "pre-filled from your chat" hint. Cleared on any manual change.
  const [ownerPrefilled, setOwnerPrefilled] = useState<boolean>(
    () => Boolean(searchParams.get('customer_id')?.trim()),
  )
  const [customerSearch, setCustomerSearch] = useState('')
  const [customerOpen, setCustomerOpen] = useState(false)
  const [showNewCustomer, setShowNewCustomer] = useState(false)
  const [newCustomerName, setNewCustomerName] = useState('')
  const [newCustomerPhone, setNewCustomerPhone] = useState('')
  const [creatingCustomer, setCreatingCustomer] = useState(false)
  const [customerError, setCustomerError] = useState<string | null>(null)
  const customerBoxRef = useRef<HTMLDivElement | null>(null)

  const [submitError, setSubmitError] = useState<string | null>(null)

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (
        customerBoxRef.current &&
        !customerBoxRef.current.contains(e.target as Node)
      ) {
        setCustomerOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  // Preview blob URL lifecycle. createObjectURL / revoke is a real
  // browser-resource pairing that must live in an effect; the null
  // branch is a derived-in-render reset (Pattern A) so the effect only
  // runs when a file actually exists.
  const [prevFile, setPrevFile] = useState<File | null>(null)
  if (file !== prevFile) {
    setPrevFile(file)
    if (!file && previewUrl !== null) setPreviewUrl(null)
  }
  useEffect(() => {
    if (!file) return
    const url = URL.createObjectURL(file)
    // eslint-disable-next-line react-hooks/set-state-in-effect -- paired with revokeObjectURL cleanup; genuine browser-resource lifecycle
    setPreviewUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [file])

  const customersQuery = useQuery({
    queryKey: ['customers'],
    enabled: isLoaded,
    queryFn: async () => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))
      const res = await fetch('/api/customers', {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(
          text || tCommon('error.request_failed', { status: res.status }),
        )
      }
      return (await res.json()) as CustomerListRow[]
    },
  })

  // Durability backstop for the conversational owner (architecture §2.1): on a
  // reload / deep-link the dashboard's client-side focus is gone, so fall back
  // to the server's recent-focus endpoint. Skipped when the paperclip handoff
  // already carried a customer_id (the live-session path).
  const focusQuery = useQuery({
    queryKey: ['dashboard-focus'],
    enabled: isLoaded && !searchParams.get('customer_id'),
    staleTime: 60_000,
    queryFn: async () => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))
      const res = await fetch('/api/eric/dashboard-focus', {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) return { focus: null } as { focus: DashboardFocus | null }
      return (await res.json()) as { focus: DashboardFocus | null }
    },
  })
  // One-shot hydrate from the backstop — render-phase guarded (Pattern A, same
  // idiom as the preview-blob lifecycle below). Never clobbers an explicit pick.
  const [focusHydrated, setFocusHydrated] = useState(false)
  if (
    !focusHydrated &&
    customerId === null &&
    focusQuery.data?.focus?.customerId
  ) {
    const f = focusQuery.data.focus
    setFocusHydrated(true)
    setCustomerId(f.customerId)
    setPrefillName(f.customerName)
    setOwnerPrefilled(true)
  }

  const filteredCustomers = useMemo(() => {
    const all = customersQuery.data ?? []
    const q = customerSearch.trim().toLowerCase()
    if (!q) return all.slice(0, 8)
    return all
      .filter((c) => {
        return (
          c.name.toLowerCase().includes(q) ||
          (c.phone ?? '').toLowerCase().includes(q) ||
          (c.email ?? '').toLowerCase().includes(q)
        )
      })
      .slice(0, 8)
  }, [customersQuery.data, customerSearch])

  const selectedCustomer = useMemo(() => {
    if (!customerId) return null
    return (customersQuery.data ?? []).find((c) => c.id === customerId) ?? null
  }, [customerId, customersQuery.data])

  // The owner to render in the chip. Prefer the fully-resolved row (has phone);
  // before the customers list loads — or if a pre-filled owner isn't in it —
  // fall back to a synthetic row from the handoff/backstop name so the owner
  // shows immediately instead of flashing the empty picker.
  const ownerCustomer = useMemo<CustomerListRow | null>(() => {
    if (selectedCustomer) return selectedCustomer
    if (customerId) {
      return { id: customerId, name: prefillName ?? '', phone: null, email: null }
    }
    return null
  }, [selectedCustomer, customerId, prefillName])

  function clearOwner() {
    setCustomerId(null)
    setPrefillName(null)
    setOwnerPrefilled(false)
    setCustomerSearch('')
    setCustomerOpen(true)
  }

  // Re-decode when VIN changes and is valid 17-char. Pattern C
  // (TanStack useQuery): isFetching is the "decoding" indicator and
  // error supplies the error string — no setState-in-effect kickoff.
  const trimmedVin = vin.trim().toUpperCase()
  const vinIsValid = isValidVin(trimmedVin)
  const vinDecodeQuery = useQuery({
    queryKey: ['vin-decode', trimmedVin],
    enabled: vinIsValid,
    queryFn: ({ signal }) => decodeVin(trimmedVin, { signal }),
  })
  const decoded: DecodedVin | null =
    vinIsValid && vinDecodeQuery.data ? vinDecodeQuery.data : null
  const vinDecoding = vinIsValid && vinDecodeQuery.isFetching
  const vinError =
    vinIsValid && vinDecodeQuery.error
      ? vinDecodeQuery.error instanceof Error
        ? vinDecodeQuery.error.message
        : t('from_photo.vin_decode_error')
      : null

  const extractMutation = useMutation({
    mutationFn: async (photo: File) => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))

      const fd = new FormData()
      fd.append('photo', photo)

      const res = await fetch('/api/vehicles/from-photo', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(
          text || tCommon('error.request_failed', { status: res.status }),
        )
      }
      return (await res.json()) as FromPhotoResponse
    },
    onSuccess: (data) => {
      setExtraction(data)
      setExtractionError(null)
      if (data.vin) setVin(data.vin)
    },
    onError: (err) => {
      setExtraction(null)
      setExtractionError(
        err instanceof Error ? err.message : t('from_photo.extract_failed'),
      )
    },
  })

  function handleFileSelected(f: File | null) {
    setFile(f)
    setExtraction(null)
    setExtractionError(null)
    setVin('')
    if (f) extractMutation.mutate(f)
  }

  function onDrop(e: React.DragEvent<HTMLLabelElement>) {
    e.preventDefault()
    setDragActive(false)
    const f = e.dataTransfer.files?.[0] ?? null
    if (f && f.type.startsWith('image/')) handleFileSelected(f)
  }

  async function handleCreateCustomer() {
    setCustomerError(null)
    if (!newCustomerName.trim()) {
      setCustomerError(t('from_photo.customer.name_required'))
      return
    }
    setCreatingCustomer(true)
    try {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))
      const res = await fetch('/api/customers', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: newCustomerName.trim(),
          phone: newCustomerPhone.trim() || null,
        }),
      })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(
          text || tCommon('error.request_failed', { status: res.status }),
        )
      }
      const created = (await res.json()) as CustomerListRow
      await queryClient.invalidateQueries({ queryKey: ['customers'] })
      setCustomerId(created.id)
      setPrefillName(created.name)
      setOwnerPrefilled(false)
      setCustomerSearch(created.name)
      setShowNewCustomer(false)
      setCustomerOpen(false)
      setNewCustomerName('')
      setNewCustomerPhone('')
    } catch (err) {
      setCustomerError(
        err instanceof Error ? err.message : t('from_photo.customer.create_failed'),
      )
    } finally {
      setCreatingCustomer(false)
    }
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))

      const trimmed = vin.trim().toUpperCase()
      const payload = {
        customer_id: customerId,
        vin: trimmed || null,
        year: decoded?.year ?? null,
        make: decoded?.make ?? null,
        model: decoded?.model ?? null,
        engine: decoded?.engine ?? null,
        photo_r2_key: extraction?.photo_r2_key ?? null,
      }

      const res = await fetch('/api/vehicles', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(
          text || tCommon('error.request_failed', { status: res.status }),
        )
      }
      return (await res.json()) as { id: string }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['vehicles'] })
      navigate(`/vehicles/${data.id}`)
    },
    onError: (err) => {
      setSubmitError(
        err instanceof Error ? err.message : t('from_photo.save_failed'),
      )
    },
  })

  // Owner is optional — Emil can save an orphan vehicle when he hasn't
  // identified the customer yet. VIN is still the gate; without one
  // there's nothing to look up downstream.
  const canSave = vinIsValid && !saveMutation.isPending

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSubmitError(null)
    if (!vinIsValid) {
      setSubmitError(t('from_photo.error.invalid_vin'))
      return
    }
    saveMutation.mutate()
  }

  return (
    <div className="space-y-6">
      <div>
        {/* §4.5 detail-page header: 44px back-button row above the title. */}
        <Link
          to="/vehicles"
          className="inline-flex min-h-11 items-center gap-1 text-sm text-muted-foreground transition-colors active:opacity-70 [@media(hover:hover)_and_(pointer:fine)]:hover:text-foreground"
        >
          {t('from_photo.back')}
        </Link>
        <h1 className="mt-1 text-[22px] font-bold tracking-[-0.015em] text-foreground [text-wrap:balance]">
          {t('from_photo.title')}
        </h1>
        <p className="mt-0.5 text-[13px] text-muted-foreground">
          {t('from_photo.subtitle')}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* ─── Upload widget ──────────────────────────────────────── */}
        <section className="rounded-[14px] border border-border bg-background p-4 sm:p-5">
          <div className="mb-3 text-[13px] font-semibold text-muted-foreground">
            {t('from_photo.upload.header')}
          </div>

          {previewUrl ? (
            <div className="space-y-3">
              <img
                src={previewUrl}
                alt={t('from_photo.upload.preview_alt')}
                className="max-h-72 w-full rounded-[10px] border border-border object-contain"
              />
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="size-4" />
                  {t('from_photo.upload.replace')}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setFile(null)
                    setExtraction(null)
                    setExtractionError(null)
                    setVin('')
                  }}
                >
                  {t('from_photo.upload.remove')}
                </Button>
              </div>
            </div>
          ) : (
            <label
              htmlFor="vin-photo"
              onClick={(e) => {
                // Explicit click forward. iOS Safari sometimes drops the
                // implicit `htmlFor` → input.click() bridge when the input
                // is sr-only — and intermittently when `capture` is set —
                // leaving the tap silently no-op. Calling .click() on the
                // ref makes the file picker open reliably. preventDefault
                // stops the (now-redundant) implicit label dispatch so the
                // dialog doesn't open twice.
                e.preventDefault()
                fileInputRef.current?.click()
              }}
              onDragOver={(e) => {
                e.preventDefault()
                setDragActive(true)
              }}
              onDragLeave={() => setDragActive(false)}
              onDrop={onDrop}
              className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-[10px] border-2 border-dashed bg-muted/40 px-6 py-10 text-center transition-[background-color,border-color] ${
                dragActive
                  ? 'border-foreground bg-muted'
                  : 'border-border [@media(hover:hover)_and_(pointer:fine)]:hover:border-input [@media(hover:hover)_and_(pointer:fine)]:hover:bg-muted'
              }`}
            >
              <Camera className="size-8 text-ink-3" />
              <div className="text-sm font-medium text-foreground">
                {t('from_photo.upload.tap_or_drop')}
              </div>
              <div className="text-xs text-muted-foreground">
                {t('from_photo.upload.formats')}
              </div>
            </label>
          )}

          {/* `capture` removed intentionally — with it set, iOS Safari opens
              the rear camera directly and skips the iOS share sheet, so the
              mechanic can't pick an existing photo from their library. Without
              it iOS shows the native picker with "Photo Library", "Take
              Photo", and "Choose File" options. Android Chrome behaves the
              same way (file chooser sheet). */}
          <input
            ref={fileInputRef}
            id="vin-photo"
            type="file"
            accept="image/*"
            className="sr-only"
            onChange={(e) => {
              const f = e.target.files?.[0] ?? null
              handleFileSelected(f)
              // Allow selecting the same file twice in a row.
              e.target.value = ''
            }}
          />

          {extractMutation.isPending ? (
            // §4.3 working line — named step, 14px ink-2 + spinner. No
            // banner box: the OCR pass is progress, not a result.
            <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2
                aria-hidden="true"
                className="size-3.5 shrink-0 animate-spin motion-reduce:animate-none"
              />
              {t('from_photo.extracting')}
            </div>
          ) : null}

          {extractionError ? (
            <div className="mt-3 rounded-[10px] border border-destructive/20 bg-destructive-soft p-3 text-sm text-destructive">
              {extractionError}
            </div>
          ) : null}

          {/* Decode outcomes are facts about the photo, not system saves or
              decision gates — neutral per §2/§4.9 (amber stays reserved for
              waiting-on-you decisions). The triangle glyph carries the
              check-this state in shape (§2 rule 2: color/copy never the
              sole signal); the decoded-facts panel below stays glyph-free
              so the two neutral surfaces read differently at a glance. */}
          {extraction && extraction.vin === null ? (
            <div className="mt-3 flex items-start gap-2 rounded-[10px] border border-border bg-muted p-3 text-sm text-foreground">
              <AlertTriangle
                aria-hidden="true"
                className="mt-0.5 size-4 shrink-0 text-muted-foreground"
              />
              <span className="min-w-0">{t('from_photo.no_vin_detected')}</span>
            </div>
          ) : null}

          {extraction && extraction.vin && extraction.confidence !== 'high' ? (
            <div className="mt-3 flex items-start gap-2 rounded-[10px] border border-border bg-muted p-3 text-sm text-foreground">
              <AlertTriangle
                aria-hidden="true"
                className="mt-0.5 size-4 shrink-0 text-muted-foreground"
              />
              <span className="min-w-0">
                {t('from_photo.confidence_warning', {
                  confidence: tCommon(`confidence.${extraction.confidence}`),
                })}
              </span>
            </div>
          ) : null}
        </section>

        {/* ─── VIN review / manual fallback ───────────────────────── */}
        <section className="rounded-[14px] border border-border bg-background p-4 sm:p-5">
          <div className="mb-3 text-[13px] font-semibold text-muted-foreground">
            {t('from_photo.vin.header')}
          </div>
          <div className="space-y-2">
            <Label htmlFor="vin">{t('from_photo.vin.label')}</Label>
            <Input
              id="vin"
              value={vin}
              onChange={(e) =>
                setVin(e.target.value.toUpperCase().replace(/\s+/g, ''))
              }
              placeholder={t('from_photo.vin.placeholder')}
              maxLength={17}
              className="w-full font-mono uppercase tabular-nums sm:w-96"
            />
            {vinDecoding ? (
              // §4.3 working line — named step, 14px ink-2 + spinner.
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2
                  aria-hidden="true"
                  className="size-3.5 shrink-0 animate-spin motion-reduce:animate-none"
                />
                {t('from_photo.vin.decoding')}
              </div>
            ) : null}
            {vinError ? (
              <div className="text-xs text-destructive">{vinError}</div>
            ) : null}
            {decoded ? (
              // Decoded vehicle facts stay neutral (§2: nothing is saved
              // yet — green would impersonate a receipt; §4.9 facts).
              <div className="mt-2 grid gap-2 rounded-[10px] border border-border bg-muted p-3 text-sm sm:grid-cols-4">
                <DecodedField
                  label={t('from_photo.vin.year')}
                  value={decoded.year ? String(decoded.year) : null}
                />
                <DecodedField
                  label={t('from_photo.vin.make')}
                  value={decoded.make}
                />
                <DecodedField
                  label={t('from_photo.vin.model')}
                  value={decoded.model}
                />
                <DecodedField
                  label={t('from_photo.vin.engine')}
                  value={decoded.engine}
                />
              </div>
            ) : null}
          </div>
        </section>

        {/* ─── Customer ──────────────────────────────────────────── */}
        <section className="rounded-[14px] border border-border bg-background p-4 sm:p-5">
          <div className="mb-3 text-[13px] font-semibold text-muted-foreground">
            {t('from_photo.customer.header')}
          </div>

          <div className="space-y-2" ref={customerBoxRef}>
            <Label htmlFor="customer-search">
              {t('from_photo.customer.label')}
            </Label>
            {ownerCustomer ? (
              // Staged owner selection — neutral ground (§2: a picked or
              // pre-filled owner is not saved; the hint line carries the
              // assistant attribution in words).
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-[10px] border border-border bg-muted p-3">
                <div className="min-w-0">
                  <div className="truncate font-semibold text-foreground">
                    {ownerCustomer.name || tCommon('loading')}
                  </div>
                  <div className="font-mono text-xs tabular-nums text-muted-foreground">
                    <PhoneLink phone={ownerCustomer.phone} />
                  </div>
                  {ownerPrefilled ? (
                    <div className="mt-1 text-xs text-muted-foreground">
                      {t('from_photo.customer.prefilled_from_eric')}
                    </div>
                  ) : null}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={clearOwner}
                >
                  {t('from_photo.customer.change')}
                </Button>
              </div>
            ) : (
              <div className="relative">
                <Input
                  id="customer-search"
                  type="search"
                  autoComplete="off"
                  value={customerSearch}
                  onChange={(e) => {
                    setCustomerSearch(e.target.value)
                    setCustomerOpen(true)
                  }}
                  onFocus={() => setCustomerOpen(true)}
                  placeholder={t('from_photo.customer.placeholder')}
                  className="w-full sm:w-96"
                />
                {customerOpen ? (
                  <div className="absolute left-0 right-0 z-20 mt-1 max-h-72 overflow-y-auto rounded-[10px] border border-border bg-background shadow-2 sm:w-96">
                    {/* "+ Create new customer" pinned to the top of the
                        dropdown so it's always reachable without scrolling
                        past the customer list. */}
                    <button
                      type="button"
                      onClick={() => {
                        setShowNewCustomer(true)
                        setCustomerOpen(false)
                        if (customerSearch.trim().length > 0) {
                          setNewCustomerName(customerSearch.trim())
                        }
                      }}
                      className="sticky top-0 z-10 flex min-h-11 w-full items-center gap-2 border-b border-border bg-muted px-3 py-2 text-left text-sm font-semibold text-foreground transition-colors active:bg-surface-2 [@media(hover:hover)_and_(pointer:fine)]:hover:bg-surface-2"
                    >
                      <Plus className="size-4" />
                      {t('from_photo.customer.create_new')}
                    </button>
                    {filteredCustomers.length === 0 ? (
                      <div className="px-3 py-3 text-sm text-muted-foreground">
                        {t('from_photo.customer.no_results')}
                      </div>
                    ) : (
                      filteredCustomers.map((c) => (
                        <button
                          type="button"
                          key={c.id}
                          onClick={() => {
                            setCustomerId(c.id)
                            setPrefillName(c.name)
                            setOwnerPrefilled(false)
                            setCustomerSearch(c.name)
                            setCustomerOpen(false)
                          }}
                          className="flex min-h-11 w-full flex-col items-start gap-0.5 px-3 py-2 text-left transition-colors active:bg-muted [@media(hover:hover)_and_(pointer:fine)]:hover:bg-muted"
                        >
                          <span className="text-sm font-medium text-foreground">
                            {c.name}
                          </span>
                          <span className="font-mono text-xs tabular-nums text-muted-foreground">
                            {formatPhone(c.phone)}
                          </span>
                        </button>
                      ))
                    )}
                  </div>
                ) : null}
              </div>
            )}
          </div>

          {showNewCustomer ? (
            <div className="mt-3 space-y-3 rounded-[10px] border border-dashed border-border bg-muted/60 p-3">
              <div className="text-[13px] font-semibold text-muted-foreground">
                {t('from_photo.customer.new_header')}
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label htmlFor="new-customer-name">
                    {t('from_photo.customer.name')}
                  </Label>
                  <Input
                    id="new-customer-name"
                    value={newCustomerName}
                    onChange={(e) => setNewCustomerName(e.target.value)}
                    placeholder={t('from_photo.customer.name_placeholder')}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="new-customer-phone">
                    {t('from_photo.customer.phone')}
                  </Label>
                  <Input
                    id="new-customer-phone"
                    type="tel"
                    value={newCustomerPhone}
                    onChange={(e) => setNewCustomerPhone(e.target.value)}
                    placeholder={t('from_photo.customer.phone_placeholder')}
                  />
                </div>
              </div>
              {customerError ? (
                <div className="text-xs text-destructive">{customerError}</div>
              ) : null}
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  size="sm"
                  onClick={handleCreateCustomer}
                  disabled={creatingCustomer}
                >
                  {creatingCustomer
                    ? tCommon('loading')
                    : t('from_photo.customer.save')}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setShowNewCustomer(false)
                    setCustomerError(null)
                  }}
                >
                  {t('from_photo.customer.cancel')}
                </Button>
              </div>
            </div>
          ) : null}
        </section>

        {submitError ? (
          <div className="rounded-[10px] border border-destructive/20 bg-destructive-soft p-3 text-sm text-destructive">
            {submitError}
          </div>
        ) : null}

        {/* ADR-0003: nothing auto-saves — this explicit tap is the write.
            Save is the ink-lane primary (§2: a human decision, not a
            system save; green arrives only after the row exists). */}
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Link
            to="/vehicles"
            className="inline-flex min-h-11 items-center text-sm text-muted-foreground transition-colors active:opacity-70 [@media(hover:hover)_and_(pointer:fine)]:hover:text-foreground"
          >
            {t('from_photo.cancel')}
          </Link>
          <Button type="submit" disabled={!canSave}>
            {saveMutation.isPending
              ? tCommon('loading')
              : t('from_photo.save')}
          </Button>
        </div>
      </form>
    </div>
  )
}

function DecodedField({
  label,
  value,
}: {
  label: string
  value: string | null
}) {
  return (
    <div>
      <div className="text-[12px] font-medium text-muted-foreground">
        {label}
      </div>
      <div className="mt-0.5 text-sm font-medium text-foreground">
        {value ?? '—'}
      </div>
    </div>
  )
}
